import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, FileText, Briefcase, MessageSquare, GraduationCap, Loader2, Upload, Crown, Lock, TrendingUp, Wand2, Link as LinkIcon, Search, Bookmark, BookmarkCheck, ClipboardList, CheckCircle2, MapPin, Compass, Target, Gauge, BookOpen, Bell, BellOff } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PastDueBanner from "../components/PastDueBanner";
import { MatchScore, MatchBreakdown } from "../components/MatchScore";

const SAMPLE = `Nadeem Ali — Toronto, Canada
Senior Supply Chain Manager with 11 years experience.
Managed warehouse operations and supply chain for Bayer Diagnostics (Healthcare/Diagnostics).
Led S&OP, demand planning and inventory optimization across North America using SAP.
Skills: SAP, S&OP, Forecasting, Demand Planning, Procurement, Inventory Management, Team Leadership.
MBA. Salary expectation: $125,000. Available immediately. Preferred countries: Canada, USA.`;

const TABS = [
  { id: "profile", label: "AI Profile", icon: FileText },
  { id: "matches", label: "My Matches", icon: Briefcase },
  { id: "browse", label: "Browse Jobs", icon: Search },
  { id: "applications", label: "Applications", icon: ClipboardList },
  { id: "assistant", label: "AI Assistant", icon: MessageSquare },
  { id: "interview", label: "Interview Prep", icon: GraduationCap },
  { id: "coach", label: "Career Coach", icon: Compass },
  { id: "premium", label: "Premium", icon: Crown },
];

const STATUS_META = {
  new: { label: "Applied", cls: "border-[#002FA7] text-[#002FA7]" },
  shortlisted: { label: "Shortlisted", cls: "border-[#7C3AED] text-[#7C3AED]" },
  interview: { label: "Interview", cls: "border-[#D97706] text-[#D97706]" },
  hired: { label: "Hired", cls: "border-[#10B981] text-[#10B981]" },
  rejected: { label: "Not selected", cls: "border-[#FF3B30] text-[#FF3B30]" },
};

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return <span className={`font-mono2 text-[11px] uppercase border px-2 py-0.5 ${m.cls}`}>{m.label}</span>;
}

function AlertToggle() {
  const [on, setOn] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/candidate/preferences").then((r) => setOn(r.data.job_alerts !== false)).catch(() => {});
  }, []);

  const toggle = async () => {
    const next = !on;
    setLoading(true);
    setOn(next);
    try {
      await api.put("/candidate/preferences", { job_alerts: next });
      toast.success(next ? "You'll get emailed about matching jobs (70%+)." : "Job match emails turned off.");
    } catch (e) {
      setOn(!next);
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      data-testid="job-alerts-toggle"
      onClick={toggle}
      disabled={loading}
      title={on ? "Email alerts for matching jobs are ON" : "Email alerts are OFF"}
      className={`font-mono2 text-xs uppercase px-3 py-1.5 inline-flex items-center gap-1.5 border transition-colors disabled:opacity-50 ${on ? "border-[#002FA7] text-[#002FA7]" : "border-[#E5E7EB] text-[#9CA3AF]"}`}
    >
      {on ? <Bell size={13} /> : <BellOff size={13} />}
      {on ? "Alerts on" : "Alerts off"}
    </button>
  );
}

export default function CandidateDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [profileReady, setProfileReady] = useState(false);
  const isPremium = user?.plan === "candidate_premium";

  const load = async () => {
    try {
      const p = await api.get("/candidate/profile");
      setProfile(p.data);
      const m = await api.get("/candidate/matches");
      setProfileReady(m.data.profile_ready);
      setMatches(m.data.matches || []);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const applyJob = async (jobId) => {
    try {
      await api.post("/candidate/apply", { job_id: jobId });
      toast.success("Application submitted!");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggleSave = async (jobId) => {
    try {
      const r = await api.post(`/candidate/saved/${jobId}`);
      toast.success(r.data.saved ? "Job saved" : "Removed from saved");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 py-8" data-testid="candidate-dashboard">
      <PastDueBanner />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Candidate Console</h1>
          <p className="text-sm text-[#4B5563]">{profile?.headline || "Build your AI profile to start matching"}</p>
        </div>
        <div className="flex items-center gap-2">
          <AlertToggle />
          {isPremium ? (
            <div data-testid="plan-badge" className="font-mono2 text-xs uppercase text-[#002FA7] border border-[#002FA7] px-3 py-1.5 inline-flex items-center gap-1.5">
              <Crown size={13} /> Premium
            </div>
          ) : (
            <Link to="/pricing" data-testid="upgrade-badge" className="font-mono2 text-xs uppercase btn-ink px-3 py-1.5 inline-flex items-center gap-1.5">
              <Crown size={13} /> Upgrade
            </Link>
          )}
          {profileReady && (
            <div className="font-mono2 text-xs uppercase text-[#4B5563] border border-[#E5E7EB] px-3 py-1.5">
              {matches.length} matches
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-px bg-[#0A0A0A] border border-[#0A0A0A] mb-8">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`cand-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-white ${tab === t.id ? "tab-active" : ""}`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab profile={profile} onParsed={() => load()} />}
      {tab === "matches" && <MatchesTab matches={matches} ready={profileReady} onApply={applyJob} onSave={toggleSave} />}
      {tab === "browse" && <BrowseTab onApply={applyJob} onSave={toggleSave} />}
      {tab === "applications" && <ApplicationsTab />}
      {tab === "assistant" && <AssistantTab />}
      {tab === "interview" && <InterviewTab matches={matches} />}
      {tab === "coach" && <CareerCoachTab isPremium={isPremium} ready={profileReady} />}
      {tab === "premium" && <PremiumTab isPremium={isPremium} ready={profileReady} />}
    </div>
  );
}

function ProfileTab({ profile, onParsed }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const parse = async () => {
    if (text.trim().length < 20) return toast.error("Paste more resume text first");
    setLoading(true);
    try {
      await api.post("/candidate/parse-resume", { resume_text: text });
      toast.success("AI profile built!");
      onParsed();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const ok = /\.(pdf|docx|txt|md)$/i.test(file.name);
    if (!ok) return toast.error("Upload a PDF, DOCX, or TXT (LinkedIn export)");
    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10MB)");
    setFileName(file.name);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/candidate/parse-resume-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Resume parsed — AI profile built!");
      onParsed();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFile(e.dataTransfer.files?.[0]);
  };

  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const importUrl = async () => {
    if (!url.trim()) return toast.error("Paste a profile or resume URL");
    setImporting(true);
    try {
      await api.post("/candidate/parse-resume-url", { url });
      toast.success("Profile imported — AI profile built!");
      onParsed();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 fade-up">
      <div className="border border-[#0A0A0A] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} className="text-[#002FA7]" />
          <h2 className="font-display font-bold text-lg">Resume AI Parser</h2>
        </div>
        <p className="text-sm text-[#4B5563] mb-4">Drop a resume, import from a LinkedIn / Indeed / portfolio URL, or paste your work history. AI extracts skills, seniority, industry & experience.</p>

        <div
          data-testid="resume-dropzone"
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`cursor-pointer border-2 border-dashed p-8 text-center transition-colors mb-4 ${dragOver ? "border-[#002FA7] bg-[#F3F4F6]" : "border-[#0A0A0A]"}`}
        >
          <input
            ref={fileRef}
            data-testid="resume-file-input"
            type="file"
            accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-[#002FA7]">
              <Loader2 size={26} className="animate-spin" />
              <span className="font-mono2 text-xs">Parsing {fileName}…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload size={26} className="text-[#002FA7]" />
              <span className="text-sm font-medium">Drag & drop your resume</span>
              <span className="font-mono2 text-xs text-[#9CA3AF]">PDF · DOCX · TXT — from LinkedIn or Indeed export · up to 10MB</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 my-3">
          <div className="h-px flex-1 bg-[#E5E7EB]" />
          <span className="font-mono2 text-xs uppercase text-[#9CA3AF]">or import from URL</span>
          <div className="h-px flex-1 bg-[#E5E7EB]" />
        </div>

        <div className="flex gap-2 mb-3">
          <input
            data-testid="resume-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && importUrl()}
            placeholder="LinkedIn / Indeed / portfolio profile URL"
            className="flex-1 border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
          <button data-testid="import-url-btn" onClick={importUrl} disabled={importing} className="btn-outline px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 whitespace-nowrap">
            {importing ? <Loader2 size={15} className="animate-spin" /> : <LinkIcon size={15} />}
            Import
          </button>
        </div>
        <p className="font-mono2 text-[11px] text-[#9CA3AF] mb-3">Tip: private LinkedIn/Indeed profiles can't be fetched — use a public URL or upload the PDF.</p>

        <div className="flex items-center gap-3 my-3">
          <div className="h-px flex-1 bg-[#E5E7EB]" />
          <span className="font-mono2 text-xs uppercase text-[#9CA3AF]">or paste text</span>
          <div className="h-px flex-1 bg-[#E5E7EB]" />
        </div>

        <textarea
          data-testid="resume-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder="Paste resume / work history here…"
          className="w-full border border-[#0A0A0A] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono2"
        />
        <div className="flex gap-3 mt-3">
          <button data-testid="parse-resume-btn" onClick={parse} disabled={loading} className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loading ? "Parsing…" : "Build AI Profile"}
          </button>
          <button data-testid="load-sample-btn" onClick={() => setText(SAMPLE)} className="btn-outline px-5 py-2.5 text-sm font-medium">
            Load sample
          </button>
        </div>
      </div>

      <div className="border border-[#0A0A0A] p-6" data-testid="parsed-profile">
        <h2 className="font-display font-bold text-lg mb-4">Professional Intelligence</h2>
        {!profile?.skills?.length ? (
          <div className="text-sm text-[#9CA3AF] font-mono2 py-10 text-center">No profile yet — parse a resume to populate.</div>
        ) : (
          <div className="space-y-4">
            <Row label="Name" value={profile.name} />
            <Row label="Headline" value={profile.headline} />
            <div className="grid grid-cols-2 gap-4">
              <Row label="Industry" value={profile.industry} />
              <Row label="Seniority" value={profile.seniority} />
              <Row label="Experience" value={`${profile.years_experience || 0}+ yrs`} />
              <Row label="Education" value={profile.education_level} />
              <Row label="Location" value={profile.location} />
              <Row label="Salary exp." value={profile.salary_expectation ? `$${Number(profile.salary_expectation).toLocaleString()}` : "—"} />
            </div>
            <div>
              <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2">Skills</div>
              <div className="flex flex-wrap gap-1.5" data-testid="profile-skills">
                {profile.skills.map((s) => (
                  <span key={s} className="font-mono2 text-xs border border-[#002FA7] text-[#002FA7] px-2 py-0.5">{s}</span>
                ))}
              </div>
            </div>
            {profile.summary && <p className="text-sm text-[#4B5563] border-l-2 border-[#002FA7] pl-3">{profile.summary}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="font-mono2 text-xs uppercase text-[#4B5563]">{label}</div>
      <div className="text-sm font-medium">{value || "—"}</div>
    </div>
  );
}

function MatchesTab({ matches, ready, onApply, onSave }) {
  const [open, setOpen] = useState(null);
  if (!ready)
    return <div className="border border-[#0A0A0A] p-10 text-center text-sm text-[#9CA3AF] font-mono2" data-testid="matches-empty">Build your AI profile first to see job matches.</div>;
  return (
    <div className="space-y-4 fade-up" data-testid="matches-list">
      {matches.map((m) => (
        <div key={m.job.id} className="border border-[#E5E7EB] hard-card">
          <div className="p-6 flex flex-col md:flex-row gap-5">
            <MatchScore score={m.score} />
            <div className="flex-1">
              <div className="font-mono2 text-xs uppercase text-[#4B5563]">{m.job.industry} · {m.job.location}</div>
              <div className="font-display font-bold text-xl">{m.job.title}</div>
              <div className="text-sm text-[#4B5563] mb-3">{m.job.company}</div>
              <ul className="space-y-1">
                {m.reasons.map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm"><span className="text-[#002FA7]">✔</span> {r}</li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(m.matched_skills || []).slice(0, 6).map((s) => (
                  <span key={s} className="font-mono2 text-xs bg-[#F3F4F6] px-2 py-0.5">{s}</span>
                ))}
              </div>
            </div>
            <div className="flex md:flex-col gap-2 justify-start">
              {m.applied ? (
                <span data-testid={`applied-${m.job.id}`} className="px-4 py-2 text-sm font-medium border border-[#10B981] text-[#10B981] inline-flex items-center gap-1.5 justify-center whitespace-nowrap">
                  <CheckCircle2 size={15} /> Applied
                </span>
              ) : (
                <button data-testid={`apply-${m.job.id}`} onClick={() => onApply(m.job.id)} className="btn-ink px-4 py-2 text-sm font-medium whitespace-nowrap">
                  Apply now
                </button>
              )}
              <button data-testid={`save-${m.job.id}`} onClick={() => onSave(m.job.id)} className="btn-outline px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 justify-center whitespace-nowrap">
                {m.saved ? <BookmarkCheck size={15} /> : <Bookmark size={15} />} {m.saved ? "Saved" : "Save"}
              </button>
              <button
                data-testid={`toggle-breakdown-${m.job.id}`}
                onClick={() => setOpen(open === m.job.id ? null : m.job.id)}
                className="px-4 py-2 text-sm font-medium text-[#4B5563] hover:text-[#0A0A0A] whitespace-nowrap"
              >
                {open === m.job.id ? "Hide" : "Breakdown"}
              </button>
            </div>
          </div>
          {open === m.job.id && (
            <div className="border-t border-[#E5E7EB] p-6 bg-[#F3F4F6]">
              <MatchBreakdown breakdown={m.breakdown} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BrowseTab({ onApply, onSave }) {
  const [filters, setFilters] = useState({ q: "", location: "", industry: "", min_salary: "", remote: false });
  const [jobs, setJobs] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (filters.remote) params.remote = "true";
      const [j, s] = await Promise.all([
        api.get("/jobs", { params }),
        api.get("/candidate/saved"),
      ]);
      setJobs(j.data);
      setSaved(s.data.map((x) => x.id));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { search(); }, []);

  const handleSave = async (id) => { await onSave(id); search(); };
  const handleApply = async (id) => { await onApply(id); };

  return (
    <div className="fade-up" data-testid="browse-tab">
      <div className="border border-[#0A0A0A] p-5 mb-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <input data-testid="search-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Title, skill, company" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <input data-testid="search-location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} placeholder="Location" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <input data-testid="search-industry" value={filters.industry} onChange={(e) => setFilters({ ...filters, industry: e.target.value })} placeholder="Industry" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <input data-testid="search-salary" type="number" value={filters.min_salary} onChange={(e) => setFilters({ ...filters, min_salary: e.target.value })} placeholder="Min salary $" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm border border-[#0A0A0A] px-3 flex-1 cursor-pointer">
            <input data-testid="search-remote" type="checkbox" checked={filters.remote} onChange={(e) => setFilters({ ...filters, remote: e.target.checked })} /> Remote
          </label>
          <button data-testid="search-btn" onClick={search} className="btn-ink px-4 py-2.5 text-sm font-medium"><Search size={16} /></button>
        </div>
      </div>

      {loading ? (
        <div className="font-mono2 text-sm text-[#9CA3AF] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Searching…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="browse-results">
          {jobs.length === 0 && <div className="text-sm text-[#9CA3AF] font-mono2">No jobs match your filters.</div>}
          {jobs.map((j) => (
            <div key={j.id} className="border border-[#E5E7EB] p-5 hard-card flex flex-col">
              <div className="font-mono2 text-xs uppercase text-[#4B5563] flex items-center gap-1.5"><MapPin size={12} /> {j.location} · {j.industry}</div>
              <div className="font-display font-bold text-lg mt-1">{j.title}</div>
              <div className="text-sm text-[#4B5563]">{j.company}</div>
              {(j.salary_min || j.salary_max) && (
                <div className="font-mono2 text-xs text-[#002FA7] mt-1">${Number(j.salary_min || 0).toLocaleString()} – ${Number(j.salary_max || 0).toLocaleString()}</div>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5 flex-1">
                {(j.required_skills || []).slice(0, 5).map((s) => <span key={s} className="font-mono2 text-xs border border-[#0A0A0A] px-2 py-0.5">{s}</span>)}
              </div>
              <div className="mt-4 flex gap-2">
                <button data-testid={`browse-apply-${j.id}`} onClick={() => handleApply(j.id)} className="btn-ink px-4 py-2 text-sm font-medium flex-1">Apply now</button>
                <button data-testid={`browse-save-${j.id}`} onClick={() => handleSave(j.id)} className="btn-outline px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5">
                  {saved.includes(j.id) ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/candidate/applications").then((r) => setApps(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="font-mono2 text-sm text-[#9CA3AF] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  if (!apps.length) return <div className="border border-[#0A0A0A] p-10 text-center text-sm text-[#9CA3AF] font-mono2" data-testid="applications-empty">No applications yet. Apply from My Matches or Browse Jobs.</div>;

  return (
    <div className="space-y-3 fade-up" data-testid="applications-list">
      {apps.map((a) => (
        <div key={a.id} className="border border-[#E5E7EB] p-5 hard-card flex items-center justify-between gap-4">
          <div>
            <div className="font-display font-bold">{a.job?.title || "Role"}</div>
            <div className="text-sm text-[#4B5563]">{a.job?.company} · {a.job?.location}</div>
            <div className="font-mono2 text-xs text-[#9CA3AF] mt-1">Applied {new Date(a.created_at).toLocaleDateString()} · {a.match_score}% match</div>
          </div>
          <StatusBadge status={a.status} />
        </div>
      ))}
    </div>
  );
}

function AssistantTab() {
  const [msgs, setMsgs] = useState([{ role: "ai", text: "Hi! Ask me anything — e.g. \"Find me jobs in Canada\" or \"What roles fit my SAP background?\"" }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [msgs, loading]);

  const send = async () => {
    if (!input.trim()) return;
    const q = input;
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/candidate/chat", { message: q, session_id: sessionRef.current });
      sessionRef.current = r.data.session_id;
      setMsgs((m) => [...m, { role: "ai", text: r.data.reply, jobs: r.data.jobs }]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-[#0A0A0A] fade-up flex flex-col h-[60vh]" data-testid="candidate-chat">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            <div className={`max-w-[80%] ${m.role === "user" ? "bg-[#F3F4F6] px-4 py-2.5" : "border border-[#0A0A0A] px-4 py-3"}`}>
              {m.role === "ai" && <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-1">AI</div>}
              <p className="text-sm whitespace-pre-wrap">{m.text}</p>
              {m.jobs?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {m.jobs.map((j) => (
                    <div key={j.job.id} className="flex items-center justify-between gap-3 text-sm border-t border-[#E5E7EB] pt-1.5">
                      <span>{j.job.title} · {j.job.company}</span>
                      <span className="font-mono2 font-bold text-[#002FA7]">{j.score}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && <div className="font-mono2 text-xs text-[#9CA3AF] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> thinking…</div>}
        <div ref={endRef} />
      </div>
      <div className="border-t border-[#0A0A0A] p-3 flex gap-2">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about jobs…"
          className="flex-1 border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
        />
        <button data-testid="chat-send" onClick={send} disabled={loading} className="btn-ink px-4 disabled:opacity-50"><Send size={18} /></button>
      </div>
    </div>
  );
}

function InterviewTab({ matches }) {
  const [jobId, setJobId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (id) => {
    setJobId(id);
    setLoading(true);
    setData(null);
    try {
      const r = await api.get(`/candidate/interview/${id}`);
      setData(r.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-up" data-testid="interview-tab">
      <div className="border border-[#0A0A0A] p-6 mb-6">
        <h2 className="font-display font-bold text-lg mb-1">AI Interview Assistant</h2>
        <p className="text-sm text-[#4B5563] mb-4">Pick a matched role to generate likely questions and STAR-method answers.</p>
        <div className="flex flex-wrap gap-2">
          {matches.slice(0, 6).map((m) => (
            <button
              key={m.job.id}
              data-testid={`interview-job-${m.job.id}`}
              onClick={() => run(m.job.id)}
              className={`border px-4 py-2 text-sm font-medium ${jobId === m.job.id ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#0A0A0A]"}`}
            >
              {m.job.title}
            </button>
          ))}
          {!matches.length && <span className="text-sm text-[#9CA3AF] font-mono2">Build your profile & get matches first.</span>}
        </div>
      </div>

      {loading && <div className="font-mono2 text-sm text-[#9CA3AF] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Generating prep…</div>}

      {data && (
        <div className="space-y-4" data-testid="interview-result">
          <div className="font-display font-bold text-xl">Prep for {data.job.title} · {data.job.company}</div>
          {(typeof data.readiness === "number" || data.strengths?.length > 0 || data.focus_areas?.length > 0) && (
            <div className="grid lg:grid-cols-3 gap-px bg-[#0A0A0A] border border-[#0A0A0A]" data-testid="interview-scorecard">
              <div className="bg-white p-5 flex flex-col items-center justify-center">
                <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2 flex items-center gap-1.5"><Gauge size={13} /> Readiness</div>
                <MatchScore score={data.readiness ?? 0} size={88} />
              </div>
              <div className="bg-white p-5">
                <div className="font-mono2 text-xs uppercase text-[#10B981] mb-2">Your strengths</div>
                <ul className="space-y-1 text-sm">{(data.strengths || []).map((s, i) => <li key={i} className="flex gap-2"><span className="text-[#10B981]">✔</span>{s}</li>)}</ul>
              </div>
              <div className="bg-white p-5">
                <div className="font-mono2 text-xs uppercase text-[#D97706] mb-2">Focus before interview</div>
                <ul className="space-y-1 text-sm">{(data.focus_areas || []).map((s, i) => <li key={i} className="flex gap-2"><span className="text-[#D97706]">!</span>{s}</li>)}</ul>
              </div>
            </div>
          )}
          {data.questions.map((q, i) => (
            <div key={i} className="border border-[#E5E7EB] p-5">
              <div className="font-medium mb-2"><span className="font-mono2 text-[#002FA7] mr-2">{String(i + 1).padStart(2, "0")}</span>{q.question}</div>
              <div className="text-sm text-[#4B5563] border-l-2 border-[#002FA7] pl-3">{q.suggested_answer}</div>
            </div>
          ))}
          {data.tips?.length > 0 && (
            <div className="border border-[#0A0A0A] p-5">
              <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-2">Tips</div>
              <ul className="space-y-1 text-sm">{data.tips.map((t, i) => <li key={i} className="flex gap-2"><span className="text-[#002FA7]">›</span>{t}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function CareerCoachTab({ isPremium, ready }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isPremium)
    return (
      <div className="border-2 border-dashed border-[#0A0A0A] p-12 text-center fade-up" data-testid="coach-locked">
        <Compass size={32} className="text-[#002FA7] mx-auto mb-4" />
        <h2 className="font-display font-black text-2xl tracking-tighter">Unlock your AI Career Coach</h2>
        <p className="text-sm text-[#4B5563] max-w-md mx-auto mt-2">
          Get a personalized growth plan — target roles, skill gaps, a curated learning path and a 30/60/90-day roadmap. Part of Candidate Premium ($19/month).
        </p>
        <Link to="/pricing" data-testid="coach-upgrade-cta" className="btn-ink px-6 py-3 font-medium inline-block mt-7">Upgrade to Premium</Link>
      </div>
    );

  if (!ready)
    return <div className="border border-[#0A0A0A] p-10 text-center text-sm text-[#9CA3AF] font-mono2">Build your AI profile first to use your career coach.</div>;

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get("/candidate/career-coach");
      setData(r.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const PRIORITY_CLS = { High: "border-[#FF3B30] text-[#FF3B30]", Medium: "border-[#D97706] text-[#D97706]", Low: "border-[#10B981] text-[#10B981]" };

  return (
    <div className="space-y-6 fade-up" data-testid="coach-tab">
      <div className="border border-[#0A0A0A] p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Compass size={18} className="text-[#002FA7]" />
            <div>
              <h2 className="font-display font-bold text-lg">AI Career Coach</h2>
              <p className="text-sm text-[#4B5563]">A personalized roadmap to level up your career.</p>
            </div>
          </div>
          <button data-testid="coach-run-btn" onClick={run} disabled={loading} className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loading ? "Coaching…" : data ? "Refresh plan" : "Build my career plan"}
          </button>
        </div>

        {data && (
          <div className="mt-6 space-y-5" data-testid="coach-result">
            <div className="border-l-2 border-[#002FA7] pl-4">
              <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-1">Where you stand</div>
              <p className="text-sm">{data.current_standing}</p>
            </div>

            {data.target_roles?.length > 0 && (
              <Block title="Target next roles">
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.target_roles.map((r, i) => (
                    <div key={i} className="border border-[#E5E7EB] p-4" data-testid={`coach-role-${i}`}>
                      <div className="font-medium flex items-center gap-2"><Target size={15} className="text-[#002FA7]" />{r.title}</div>
                      <p className="text-sm text-[#4B5563] mt-1">{r.why}</p>
                    </div>
                  ))}
                </div>
              </Block>
            )}

            {data.skill_gaps?.length > 0 && (
              <Block title="Skill gaps to close">
                <div className="space-y-2">
                  {data.skill_gaps.map((g, i) => (
                    <div key={i} className="flex items-start gap-3 border border-[#E5E7EB] p-3">
                      <span className={`font-mono2 text-[10px] uppercase border px-1.5 py-0.5 shrink-0 mt-0.5 ${PRIORITY_CLS[g.priority] || PRIORITY_CLS.Medium}`}>{g.priority}</span>
                      <div><span className="font-medium text-sm">{g.skill}</span><p className="text-sm text-[#4B5563]">{g.why}</p></div>
                    </div>
                  ))}
                </div>
              </Block>
            )}

            {data.learning_plan?.length > 0 && (
              <Block title="Recommended learning path">
                <div className="space-y-2">
                  {data.learning_plan.map((l, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <BookOpen size={15} className="text-[#002FA7] shrink-0 mt-0.5" />
                      <div><span className="font-medium">{l.resource}</span> <span className="font-mono2 text-[11px] uppercase text-[#9CA3AF]">· {l.type}</span><p className="text-[#4B5563]">{l.outcome}</p></div>
                    </div>
                  ))}
                </div>
              </Block>
            )}

            {data.roadmap?.length > 0 && (
              <div>
                <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2">90-day roadmap</div>
                <div className="grid sm:grid-cols-3 gap-px bg-[#0A0A0A] border border-[#0A0A0A]">
                  {data.roadmap.map((r, i) => (
                    <div key={i} className="bg-white p-4" data-testid={`coach-roadmap-${i}`}>
                      <div className="font-mono2 font-bold text-[#002FA7] mb-2">{r.horizon}</div>
                      <ul className="space-y-1 text-sm">{(r.actions || []).map((a, j) => <li key={j} className="flex gap-2"><span className="text-[#002FA7]">›</span>{a}</li>)}</ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.north_star && (
              <div className="border border-[#002FA7] bg-[#002FA7]/5 p-4 text-sm font-medium flex gap-2"><Sparkles size={16} className="text-[#002FA7] shrink-0 mt-0.5" />{data.north_star}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function PremiumTab({ isPremium, ready }) {
  const [opt, setOpt] = useState(null);
  const [salary, setSalary] = useState(null);
  const [loadingOpt, setLoadingOpt] = useState(false);
  const [loadingSal, setLoadingSal] = useState(false);

  if (!isPremium)
    return (
      <div className="border-2 border-dashed border-[#0A0A0A] p-12 text-center fade-up" data-testid="premium-locked">
        <Lock size={32} className="text-[#002FA7] mx-auto mb-4" />
        <h2 className="font-display font-black text-2xl tracking-tighter">Unlock Candidate Premium</h2>
        <p className="text-sm text-[#4B5563] max-w-md mx-auto mt-2">
          Get AI Resume Optimization, salary insights, an AI career coach and priority visibility to recruiters — for $19/month.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 max-w-lg mx-auto mt-6 text-left">
          {["AI Resume Optimization & ATS score", "Personalized salary insights", "Priority visibility in shortlists", "AI career coaching"].map((f) => (
            <div key={f} className="flex gap-2 text-sm"><Crown size={15} className="text-[#002FA7] shrink-0 mt-0.5" />{f}</div>
          ))}
        </div>
        <Link to="/pricing" data-testid="premium-upgrade-cta" className="btn-ink px-6 py-3 font-medium inline-block mt-7">Upgrade to Premium</Link>
      </div>
    );

  if (!ready)
    return <div className="border border-[#0A0A0A] p-10 text-center text-sm text-[#9CA3AF] font-mono2">Build your AI profile first to use premium tools.</div>;

  const optimize = async () => {
    setLoadingOpt(true);
    try {
      const r = await api.post("/candidate/optimize-resume");
      setOpt(r.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoadingOpt(false);
    }
  };

  const getSalary = async () => {
    setLoadingSal(true);
    try {
      const r = await api.get("/candidate/salary-insight");
      setSalary(r.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoadingSal(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="premium-tab">
      <div className="border border-[#0A0A0A] p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Wand2 size={18} className="text-[#002FA7]" />
            <h2 className="font-display font-bold text-lg">AI Resume Optimization</h2>
          </div>
          <button data-testid="optimize-resume-btn" onClick={optimize} disabled={loadingOpt} className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {loadingOpt ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loadingOpt ? "Analyzing…" : opt ? "Re-run" : "Optimize my resume"}
          </button>
        </div>

        {opt && (
          <div className="mt-5 grid lg:grid-cols-3 gap-5" data-testid="optimize-result">
            <div className="lg:col-span-1 flex flex-col items-center justify-center border border-[#E5E7EB] p-5">
              <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2">ATS Score</div>
              <MatchScore score={opt.ats_score} size={96} />
            </div>
            <div className="lg:col-span-2 space-y-4">
              <Block title="Optimized summary" border>
                <p className="text-sm">{opt.optimized_summary}</p>
              </Block>
              <div className="grid sm:grid-cols-2 gap-4">
                <Block title="Strengths">
                  <ul className="space-y-1">{opt.strengths.map((s, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[#10B981]">✔</span>{s}</li>)}</ul>
                </Block>
                <Block title="Gaps to fix">
                  <ul className="space-y-1">{opt.gaps.map((s, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[#FF3B30]">!</span>{s}</li>)}</ul>
                </Block>
              </div>
              <Block title="Action suggestions">
                <ul className="space-y-1">{opt.suggestions.map((s, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[#002FA7]">›</span>{s}</li>)}</ul>
              </Block>
              {opt.recommended_skills?.length > 0 && (
                <div>
                  <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2">Add these in-demand skills</div>
                  <div className="flex flex-wrap gap-1.5">{opt.recommended_skills.map((s) => <span key={s} className="font-mono2 text-xs border border-[#002FA7] text-[#002FA7] px-2 py-0.5">{s}</span>)}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="border border-[#0A0A0A] p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-[#002FA7]" />
            <h2 className="font-display font-bold text-lg">Salary Insights</h2>
          </div>
          <button data-testid="salary-insight-btn" onClick={getSalary} disabled={loadingSal} className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {loadingSal ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loadingSal ? "Calculating…" : salary ? "Refresh" : "Get my salary range"}
          </button>
        </div>

        {salary && (
          <div className="mt-5 space-y-4" data-testid="salary-result">
            <div className="grid grid-cols-3 gap-px bg-[#0A0A0A] border border-[#0A0A0A]">
              {[["Low", salary.low], ["Median", salary.mid], ["High", salary.high]].map(([l, v]) => (
                <div key={l} className="bg-white p-5 text-center">
                  <div className="font-mono2 text-xs uppercase text-[#4B5563]">{l}</div>
                  <div className="font-mono2 font-bold text-2xl text-[#002FA7]">{salary.currency} {Number(v).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm"><span className="font-mono2 text-xs uppercase text-[#4B5563]">Market demand:</span><span className="font-medium text-[#10B981]">{salary.market_demand}</span></div>
            <p className="text-sm text-[#4B5563] border-l-2 border-[#002FA7] pl-3">{salary.rationale}</p>
            {salary.negotiation_tips?.length > 0 && (
              <Block title="Negotiation tips">
                <ul className="space-y-1">{salary.negotiation_tips.map((t, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[#002FA7]">›</span>{t}</li>)}</ul>
              </Block>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Block({ title, children, border }) {
  return (
    <div className={border ? "border border-[#E5E7EB] p-4" : ""}>
      <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2">{title}</div>
      {children}
    </div>
  );
}
